// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import { formatBytes } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * Every upload in the app, docked in the corner. It is mounted by the dashboard rather than by a
 * screen, so walking from the drive to the pipeline does not interrupt a 200 MB video — the whole
 * reason it exists rather than a modal, which would hold the person still until the bytes landed.
 *
 * It renders nothing at all when there is nothing to say.
 */
export default function UploadTray() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { items, isOpen, setOpen, clearFinished, cancel, retry } = useUploads();

    const active = items.filter((item) => item.status === "waiting" || item.status === "uploading");

    /**
     * A `File` handle cannot be written down and read back, so a reload abandons whatever is in flight
     * — the bytes are still on their disk, but the app has no way to reach them again without the
     * person picking the file a second time. This is the browser's own "leave site?" prompt, and it is
     * the only thing standing between an accidental refresh and a lost 200 MB upload.
     */
    useEffect(() => {
        if (!active.length) return;
        const warn = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [active.length]);

    if (!items.length) return null;
    const failed = items.filter((item) => item.status === "failed");
    const settled = items.length - active.length;

    const title = active.length
        ? t.uploads.uploading.replace("{done}", String(settled)).replace("{total}", String(items.length))
        : failed.length
          ? t.uploads.someFailed.replace("{count}", String(failed.length))
          : t.uploads.allDone;

    return (
        <section className={`upload-tray${isOpen ? "" : " upload-tray--closed"}`}>
            <header className="upload-tray__head">
                <Icon name="upload" size={18} />
                <span className="upload-tray__title">{title}</span>

                {!active.length && (
                    <button type="button" className="upload-tray__head-action" onClick={clearFinished} title={t.uploads.clear}>
                        <Icon name="close" size={14} />
                    </button>
                )}
                <button
                    type="button"
                    className="upload-tray__toggle"
                    onClick={() => setOpen(!isOpen)}
                    aria-label={isOpen ? t.uploads.collapse : t.uploads.expand}
                >
                    <Icon name="chevron" size={14} />
                </button>
            </header>

            <ul className="upload-tray__list">
                {items.map((item) => (
                    <li key={item.id} className={`upload-tray__item upload-tray__item--${item.status}`}>
                        <div className="upload-tray__row">
                            <span className="upload-tray__name" title={item.name}>
                                {item.name}
                            </span>

                            {item.status === "uploading" && <span className="upload-tray__percent">{item.progress}%</span>}
                            {item.status === "waiting" && <span className="upload-tray__percent">{t.uploads.waiting}</span>}
                            {item.status === "done" && <Icon name="check" size={14} />}

                            {/* Stopping a half-sent file is the whole point of watching it go. */}
                            {(item.status === "uploading" || item.status === "waiting") && (
                                <button type="button" className="upload-tray__action" onClick={() => cancel(item.id)} title={t.common.cancel}>
                                    <Icon name="close" size={12} />
                                </button>
                            )}
                            {item.status === "failed" && (
                                <button type="button" className="upload-tray__action" onClick={() => retry(item.id)} title={t.uploads.retry}>
                                    <Icon name="refresh" size={12} />
                                </button>
                            )}
                        </div>

                        {(item.status === "uploading" || item.status === "waiting") && (
                            <div className="upload-tray__bar">
                                <span className="upload-tray__bar-fill" style={{ width: `${item.progress}%` }} />
                            </div>
                        )}

                        <span className="upload-tray__meta">
                            {item.status === "failed" ? item.error : item.status === "cancelled" ? t.uploads.cancelled : formatBytes(item.size, lang)}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
}
